import { useState } from "react";
import axios from "axios";
import { PublicShell } from "@/components/PublicShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEVEL_DOT = {
  1: "bg-rose-500",
  2: "bg-orange-400",
  3: "bg-amber-400",
  4: "bg-emerald-400",
  5: "bg-emerald-600",
};
const LEVEL_LABEL = { 1: "Ad hoc", 2: "Repeatable", 3: "Defined", 4: "Managed", 5: "Optimising" };
const ROLES = ["CRO", "CISO", "PMO Director", "Transformation Director", "CFO", "CEO", "Consultant / Advisor", "Other"];
const INDUSTRIES = [
  "Financial Services", "Manufacturing", "Energy & Utilities",
  "Healthcare & Life Sciences", "Public Sector", "Retail & Consumer",
  "Technology & SaaS", "Aerospace & Defence", "Professional Services",
  "Logistics & Supply Chain", "Other",
];

const SAMPLE = `risk_id,title,owner_user_id,inherent_score,residual_score,updated_at,linked_signal_ids,theme_id
R-001,AWS eu-west-1 outage,u_alice,20,15,2026-06-01T10:00:00Z,s_aws_001,t_operational
R-002,Tier-1 supplier ransomware,,18,18,2025-02-01T10:00:00Z,,
R-003,GDPR DPIA overdue,u_bob,16,10,2026-06-10T10:00:00Z,s_gdpr_001,t_compliance`;

function parseCsv(text) {
  const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
  if (lines.length < 2) return [];
  const header = lines[0].split(",").map((h) => h.trim());
  return lines.slice(1).map((line) => {
    const cells = line.split(",").map((c) => c.trim());
    const row = {};
    header.forEach((h, i) => {
      let v = cells[i];
      if (v === undefined || v === "") return;
      if (h === "linked_signal_ids") v = v.split("|").filter(Boolean);
      if (h === "inherent_score" || h === "residual_score") v = Number(v);
      row[h] = v;
    });
    return row;
  });
}

function parseInput(raw) {
  const trimmed = raw.trim();
  if (!trimmed) return [];
  try {
    const j = JSON.parse(trimmed);
    if (Array.isArray(j)) return j;
    if (Array.isArray(j.risks)) return j.risks;
  } catch (_) { /* fall through to CSV */ }
  return parseCsv(trimmed);
}

export default function RiskHealthCheck() {
  const [raw, setRaw] = useState("");
  const [organisation, setOrganisation] = useState("");
  const [role, setRole] = useState("");
  const [email, setEmail] = useState("");
  const [industry, setIndustry] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [rowCount, setRowCount] = useState(0);

  const onChange = (e) => {
    const v = e.target.value;
    setRaw(v);
    setRowCount(parseInput(v).length);
  };

  const submit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const risks = parseInput(raw);
      if (risks.length === 0) {
        setError("Paste at least one risk row (CSV or JSON).");
        setSubmitting(false);
        return;
      }
      const { data } = await axios.post(`${API}/risk-health-check/score`, {
        risks,
        organisation: organisation || null,
        role: role || null,
        email: email || null,
        industry: industry || null,
      });
      setResult(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setError(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Scoring failed — try again");
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    const s = result.scorecard;
    return (
      <PublicShell active="/risk-health-check">
        <div className="bg-gradient-to-br from-slate-50 to-violet-50 px-6 py-12 pb-40">
          <div className="mx-auto max-w-4xl">
          <div className="rounded-2xl bg-white p-8 shadow-xl ring-1 ring-violet-100" data-testid="rhc-scorecard">
            <p className="text-xs uppercase tracking-[0.2em] text-violet-600 font-semibold">EKSELENS · ERIO · Risk Register Health Check</p>
            <div className="mt-3 flex items-baseline gap-4">
              <h1 className="text-5xl font-bold tracking-tight text-violet-900" data-testid="rhc-overall-level">Level {s.overall_level}</h1>
              <span className="text-2xl font-semibold text-slate-700">{s.overall_level_label}</span>
            </div>
            <p className="mt-2 text-sm text-slate-500">Composite score <strong>{s.overall_score.toFixed(3)}</strong> · Confidence <strong>{s.confidence}</strong> · {s.sample_size} risks scored · Charter: <strong className="text-violet-700">{result.remediation.provenance}</strong></p>
          </div>

          <div className="mt-6 grid gap-4 md:grid-cols-2">
            {s.checks.map((c) => (
              <div key={c.check_id} className="rounded-xl bg-white p-5 shadow-sm ring-1 ring-slate-100" data-testid={`rhc-check-${c.check_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-semibold text-slate-800">{c.name}</h3>
                    <p className="text-xs text-slate-500 mt-0.5">{c.pmo360_dimension} · weight {c.weight}</p>
                  </div>
                  <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium text-white ${LEVEL_DOT[c.score >= 0.85 ? 5 : c.score >= 0.7 ? 4 : c.score >= 0.5 ? 3 : c.score >= 0.3 ? 2 : 1]}`}>
                    {(c.score * 100).toFixed(0)}%
                  </span>
                </div>
                <p className="mt-2 text-sm text-slate-600">{c.verdict}</p>
              </div>
            ))}
          </div>

          {result.top_gaps.length > 0 && (
            <div className="mt-6 rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-100" data-testid="rhc-gaps">
              <h3 className="text-lg font-semibold text-violet-900">Top {result.top_gaps.length} evidence gaps</h3>
              <p className="text-xs text-slate-500 mb-4">Charter-compliant remediation hints — anchored to specific rules, no model invention.</p>
              <ul className="space-y-4">
                {result.top_gaps.map((g) => (
                  <li key={g.check_id} className="rounded-lg border-l-4 border-violet-400 bg-violet-50/40 px-4 py-3">
                    <p className="font-medium text-slate-800">{g.name} · <span className="text-xs text-slate-500">{g.verdict}</span></p>
                    <ul className="mt-2 list-disc pl-5 text-sm text-slate-700 space-y-1">
                      {(result.remediation.hints[g.check_id] || []).map((b, i) => <li key={i}>{b}</li>)}
                    </ul>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-6 flex flex-wrap gap-3">
            <a href="/pilot" className="rounded-lg bg-violet-700 px-5 py-2.5 text-sm font-medium text-white hover:bg-violet-800" data-testid="rhc-cta-pilot">Book a pilot to deepen this scoring</a>
            <a href="/maturity-assessment" className="rounded-lg border border-slate-300 px-5 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-100" data-testid="rhc-cta-maturity">Take the PMO maturity assessment</a>
            <button onClick={() => { setResult(null); setRaw(""); setRowCount(0); }} className="rounded-lg border border-slate-300 px-5 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-100" data-testid="rhc-reset-btn" id="rhc-reset">Run another check</button>
          </div>
          </div>
        </div>
      </PublicShell>
    );
  }

  return (
    <PublicShell active="/risk-health-check">
      <div className="bg-gradient-to-br from-slate-50 to-violet-50 px-6 py-12 pb-40">
        <div className="mx-auto max-w-3xl">
        <p className="text-xs uppercase tracking-[0.2em] text-violet-600 font-semibold">EKSELENS · ERIO · Lead-magnet</p>
        <h1 className="mt-2 text-4xl sm:text-5xl font-bold tracking-tight text-violet-950">Risk Register Health Check</h1>
        <p className="mt-4 text-base text-slate-700 max-w-2xl">Paste your risk register (CSV or JSON). We&rsquo;ll score it on six dimensions — completeness, freshness, ownership, evidence, control linkage, scoring discipline — and surface the top three gaps. No login. Defensible AI Charter applies.</p>

        <div className="mt-6 rounded-xl bg-white p-5 shadow-sm ring-1 ring-slate-100">
          <label className="text-sm font-medium text-slate-700">Risk register (CSV or JSON array)</label>
          <textarea
            value={raw}
            onChange={onChange}
            data-testid="rhc-input"
            rows={10}
            placeholder={SAMPLE}
            className="mt-2 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 font-mono text-xs text-slate-800 focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-200"
          />
          <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
            <span><strong data-testid="rhc-row-count">{rowCount}</strong> row(s) detected</span>
            <button type="button" onClick={() => { setRaw(SAMPLE); setRowCount(parseInput(SAMPLE).length); }} className="text-violet-700 hover:underline" data-testid="rhc-use-sample">Use sample data</button>
          </div>
        </div>

        <div className="mt-6 rounded-xl bg-white p-5 shadow-sm ring-1 ring-slate-100">
          <h3 className="font-semibold text-violet-900">Optional — get the full PDF</h3>
          <p className="text-xs text-slate-500 mb-3">If you&rsquo;d like the full role-tuned report by email, leave your details.</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <input value={organisation} onChange={(e) => setOrganisation(e.target.value)} placeholder="Organisation" className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-200" data-testid="rhc-org" />
            <select value={role} onChange={(e) => setRole(e.target.value)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-200" data-testid="rhc-role">
              <option value="">Your role…</option>
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <select value={industry} onChange={(e) => setIndustry(e.target.value)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-200" data-testid="rhc-industry">
              <option value="">Your industry… (optional)</option>
              {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
            </select>
            <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Work email" type="email" className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-200" data-testid="rhc-email" />
          </div>
        </div>

        {error && <p className="mt-4 rounded-lg bg-rose-50 px-4 py-2 text-sm text-rose-700" data-testid="rhc-error">{error}</p>}

        <div className="mt-6 flex items-center gap-3">
          <button onClick={submit} disabled={submitting || rowCount === 0} className="rounded-lg bg-violet-700 px-6 py-3 text-sm font-semibold text-white hover:bg-violet-800 disabled:opacity-50" data-testid="rhc-submit">
            {submitting ? "Scoring…" : "Score my register"}
          </button>
          <p className="text-xs text-slate-500">No data is retained beyond the scorecard itself.</p>
        </div>

        <p className="mt-12 text-xs text-slate-400">Anchored to EKSELENS PMO360 · Defensible AI Charter applies · No login required</p>
      </div>
      </div>
    </PublicShell>
  );
}
